import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Collapse,
  ToggleButtonGroup, ToggleButton, Tooltip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  Button, Select, MenuItem, FormControl, InputLabel, Slider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getEfficientFrontier, EfficientFrontierResponse, EfficientFrontierPoint, IndividualAsset, BenchmarkAsset } from '../services/api';
import { runJob, watchJob, jobKey } from '../services/backgroundJobs';
import { getTickers as getPortfolioSymbols, getOrders as getStoredOrders } from '../utils/portfolioStorage';

const ANALYTICS_TICKERS_KEY = 'analytics_tickers';

// Helper to load tickers from localStorage (analytics-specific tickers, not portfolio)
const loadTickers = (): string[] => {
  try {
    const stored = localStorage.getItem(ANALYTICS_TICKERS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Share counts per symbol from stored orders. Sent as counts, not weights: the
// server multiplies by each stock's latest (currency-converted) price, so the
// portfolio is weighted by VALUE. Dividing shares by total shares, as this used
// to, weighted 10 shares of a 1,000 stock the same as 10 shares of a 10 stock.
const calculatePortfolioShares = (symbols: string[]): { [symbol: string]: number } | null => {
  try {
    const allOrders = getStoredOrders();
    if (Object.keys(allOrders).length === 0) return null;
    const shares: { [symbol: string]: number } = {};
    for (const symbol of symbols) {
      const total = (allOrders[symbol] || []).reduce((sum: number, order: any) => sum + (order.shares || 0), 0);
      if (total > 0) shares[symbol] = total;
    }
    return Object.keys(shares).length ? shares : null;
  } catch (error) {
    console.error('Error reading portfolio share counts:', error);
    return null;
  }
};

export interface ApplyWeightsMeta {
  strategy: string;        // label, e.g. "Max Sharpe"
  capPct: number;          // position cap in %, 100 = uncapped
  period: string;          // estimation window, e.g. "1Y"
  needsForecast: boolean;
  fallback: boolean;       // optimiser fell back to equal weight
}

interface EfficientFrontierProps {
  symbols?: string[];
  userWeights?: { [symbol: string]: number };
  onReset?: () => void;
  /** When given, the selected strategy gets an "Apply these weights" button. */
  onApplyWeights?: (weights: Record<string, number>, meta: ApplyWeightsMeta) => void;
  /** Pop-up label and link when the calculation finishes while the user is elsewhere. */
  jobLabel?: string;
  jobRoute?: string;
}

/** A strategy's weights as holdings: slivers under 0.1% dropped, the rest renormalised to exactly 100%. */
function toHoldingWeights(raw: Record<string, number>): Record<string, number> {
  const kept = Object.entries(raw).filter(([, w]) => w >= 0.001);
  const total = kept.reduce((a, [, w]) => a + w, 0) || 1;
  const out: Record<string, number> = {};
  kept.forEach(([sym, w]) => { out[sym] = Math.round((w / total) * 10000) / 10000; });
  const residual = Math.round((1 - Object.values(out).reduce((a, w) => a + w, 0)) * 10000) / 10000;
  const top = kept.sort((a, b) => b[1] - a[1])[0]?.[0];
  if (top && residual) out[top] = Math.round((out[top] + residual) * 10000) / 10000;
  return out;
}

interface RfRateOption {
  code: string;
  label: string;
  description: string;
  rate: number | null;
  live?: boolean;
}

// The six optimization strategies, in the order they're always presented —
// shared by the chart, the strategy cards, and the benchmark table so a
// given strategy is always the same color everywhere on the page.
// Ordered by how much each strategy depends on forecasting returns, not by
// how impressive it sounds. The five robust strategies come first; Max Sharpe
// is last because it is the only one that needs expected returns, and those are
// estimated here as a 1-year sample mean — the classic Markowitz instability.
// It stays available and clearly labelled rather than being removed.
const STRATEGY_DEFS: { key: keyof EfficientFrontierResponse; label: string; color: string; needsForecast?: boolean }[] = [
  { key: 'risk_parity_portfolio',         label: 'Risk Parity',        color: '#2196f3' },
  { key: 'min_variance_portfolio',        label: 'Min Variance',       color: '#4caf50' },
  { key: 'min_cvar_portfolio',            label: 'Min CVaR',           color: '#ff9800' },
  { key: 'max_diversification_portfolio', label: 'Max Diversification', color: '#9c27b0' },
  { key: 'inverse_volatility_portfolio',  label: 'Inverse Volatility', color: '#00bcd4' },
  { key: 'max_sharpe_portfolio',          label: 'Max Sharpe',         color: '#f44336', needsForecast: true },
];

const FORECAST_WARNING =
  'Depends on expected returns, estimated from the trailing 1-year mean. That input is ' +
  'notoriously unstable — this allocation can change materially month to month and tends ' +
  'to concentrate in whatever performed best recently. The other strategies need no return ' +
  'forecast at all.';

// Holdings are shown as a ranked list rather than colour-coded bar segments:
// with a colour per stock, reading an allocation meant decoding a legend, small
// weights became slivers, and comparing one stock across strategies meant
// hunting for its colour at six different positions.

// Rows under this are named in a "not held" line instead of drawn as slivers.
const SHOW_MIN = 0.005;
// Long lists show the largest holdings until they cover this share of the
// portfolio (at least COLLAPSE_MIN_ROWS rows), then fold the tail into one
// expandable line. A fold of one or two rows saves nothing, so it only happens
// when at least COLLAPSE_MIN_TAIL rows would be hidden.
const COLLAPSE_COVER = 0.9;
const COLLAPSE_MIN_ROWS = 8;
const COLLAPSE_MIN_TAIL = 3;
// How far outside the forecast-free range a weight must sit before it is
// flagged. Absolute, in weight units: 2 percentage points.
const RANGE_TOL = 0.02;
const WARN = '#f59e0b';
// Flag a stock when its plotted return (mean daily x 252) and its actual
// compound return differ by at least this many percentage points.
const RETURN_GAP_FLAG = 15;

// The strategies that need no return forecast. Each stock's weight is compared
// with the range these give it.
//
// Why a range and not "held by N of 5": Risk Parity and Inverse Volatility
// weight every stock by roughly 1/volatility, so they hold everything by
// construction, and under a position cap the other three rarely zero anything
// either. Measured on an 8-stock basket, every row in every strategy scored 5/5
// — including MSFT at a 1.5% Max Sharpe weight. The signal is in *how much*
// they hold: JNJ was 25% in Max Sharpe against 13-18% across the other five.
const ROBUST_DEFS = STRATEGY_DEFS.filter((d) => !d.needsForecast);

// ──────────────────────────────────────────────────────────────────────────
// Native risk/return scatter — replaces the old server-rendered Plotly
// <iframe>. Plain SVG (no chart library): a dashed frontier curve traced
// through the real optimizer output, individual assets as hollow squares,
// and all six strategies visible as colored dots simultaneously (the old
// version only showed whichever one you'd clicked). Colors route through
// the app's --bb-* tokens so light/dark follow the system theme for free —
// this is real DOM/SVG, not canvas, so CSS variables resolve normally.
// ──────────────────────────────────────────────────────────────────────────
// "Nice" tick step: 1, 2, 2.5 or 5 x 10^k, so axes read 0/10/20/30 rather
// than the 6/14/22/29 that dividing a padded range into equal parts produced.
const niceStep = (span: number, targetTicks: number): number => {
  const raw = span / Math.max(1, targetTicks);
  if (!(raw > 0)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
};

const FrontierChart: React.FC<{
  data: EfficientFrontierResponse;
  selectedKey: string;
  onSelectKey: (key: string) => void;
  /** Stocks the selected strategy holds; the rest are faded. */
  heldSymbols: Set<string>;
  /** Stock under the pointer in the holdings list, highlighted here. */
  hoveredSymbol: string | null;
}> = ({ data, selectedKey, onSelectKey, heldSymbols, hoveredSymbol }) => {
  const accent = STRATEGY_DEFS.find((d) => d.key === selectedKey)?.color || 'var(--bb-accent)';

  // Drawn at 1:1 pixel scale from the measured container width, not a fixed
  // viewBox stretched to fit — so text stays its real size and the chart can
  // be wide and short in the stacked layout.
  const wrapRef = useRef<HTMLDivElement>(null);
  const clipId = useRef(`ef-clip-${Math.random().toString(36).slice(2)}`).current;
  const [W, setW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setW(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [showAll, setShowAll] = useState(false);
  const H = Math.round(Math.min(460, Math.max(320, W * 0.38)));
  const padL = 58, padR = 28, padT = 34, padB = 46;   // padT leaves room for the zoom toggle

  const strategyPoints = STRATEGY_DEFS
    .map((def) => ({ ...def, point: data[def.key] as EfficientFrontierPoint | null | undefined }))
    .filter((s) => s.point);
  const rf = data.risk_free_rate;
  const ms = data.max_sharpe_portfolio;
  const benchAll = (data.benchmarks || []).filter((b) => Number.isFinite(b.risk) && Number.isFinite(b.return));
  const isPortfolio = (b: BenchmarkAsset) => b.tier === 'portfolio' || b.symbol === 'USER_PORTFOLIO';
  const portfolioPts = benchAll.filter(isPortfolio);
  const marketBench = benchAll.filter((b) => !isPortfolio(b));
  const period = (data.period || '1y').toUpperCase();
  const ccyTag = data.currency ? ` IN ${data.currency}` : '';

  // ── What the view is about ──
  // Core: the frontier, the six strategies and your portfolio. Stocks and
  // benchmarks join the view when they sit near it; the rest become arrows on
  // the border ("Show all" brings them in). The risk axis is NOT forced to
  // start at 0: doing that for the risk-free point left a basket of 45-60%-vol
  // miners squeezed into a corner, with the Capital Market Line crossing an
  // otherwise empty chart.
  const coreRisk = [...data.frontier_points.map((p) => p.risk), ...strategyPoints.map((s) => s.point!.risk), ...portfolioPts.map((b) => b.risk)];
  const coreRet = [...data.frontier_points.map((p) => p.return), ...strategyPoints.map((s) => s.point!.return), ...portfolioPts.map((b) => b.return)];
  const cRiskMin = Math.min(...coreRisk), cRiskMax = Math.max(...coreRisk, 1);
  const cRetMin = Math.min(...coreRet), cRetMax = Math.max(...coreRet);
  const retBand = Math.max(cRetMax - cRetMin, 50);
  // Generous for stocks (they are the universe): out only if far more volatile
  // than anything on the frontier, or wildly outside its return range.
  const stockNear = (risk: number, ret: number) =>
    risk <= cRiskMax * 1.35 && ret >= cRetMin - retBand && ret <= cRetMax + retBand;
  // Benchmarks also go to the edge when they're far *less* volatile than the
  // frontier — FTSE 100 at 12% next to a 45% frontier stretched the axis.
  const benchNear = (risk: number, ret: number) => stockNear(risk, ret) && risk >= cRiskMin * 0.5;
  const inView = showAll ? data.individual_assets : data.individual_assets.filter((a) => stockNear(a.risk, a.return));
  const benchInView = showAll ? marketBench : marketBench.filter((b) => benchNear(b.risk, b.return));
  const offStocks = showAll ? [] : data.individual_assets.filter((a) => !stockNear(a.risk, a.return));
  const offBench = showAll ? [] : marketBench.filter((b) => !benchNear(b.risk, b.return));
  const anyOutside = data.individual_assets.some((a) => !stockNear(a.risk, a.return)) || marketBench.some((b) => !benchNear(b.risk, b.return));

  // ── Domains: padded by a share of the span, snapped to round ticks ──
  const allRisk = [...coreRisk, ...inView.map((a) => a.risk), ...benchInView.map((b) => b.risk)];
  const allReturn = [...coreRet, ...inView.map((a) => a.return), ...benchInView.map((b) => b.return)];
  const rkMin = Math.min(...allRisk), rkMax = Math.max(...allRisk);
  const rkPad = (rkMax - rkMin) * 0.05 || 1;
  const riskStep = niceStep(rkMax - rkMin + 2 * rkPad, W > 900 ? 10 : 5);
  const riskHi = Math.ceil((rkMax + rkPad) / riskStep) * riskStep;
  let riskLo = Math.max(0, Math.floor((rkMin - rkPad) / riskStep) * riskStep);
  if (riskLo <= riskHi * 0.25) riskLo = 0;   // close to zero anyway: start there, it reads naturally
  const rMin = Math.min(...allReturn), rMax = Math.max(...allReturn);
  const rPad = (rMax - rMin) * 0.05 || 1;
  const retStep = niceStep(rMax - rMin + 2 * rPad, 5);
  const retLo = Math.floor((rMin - rPad) / retStep) * retStep;
  const retHi = Math.ceil((rMax + rPad) / retStep) * retStep;
  const fmtTick = (v: number, step: number) => `${v.toFixed(step < 1 ? 1 : 0)}%`;
  const ticks = (lo: number, hi: number, step: number) => {
    const out: number[] = [];
    for (let v = lo; v <= hi + step * 1e-6; v += step) out.push(Math.abs(v) < step * 1e-6 ? 0 : v);
    return out;
  };

  const x = (r: number) => padL + ((r - riskLo) / (riskHi - riskLo || 1)) * (W - padL - padR);
  const y = (r: number) => H - padB - ((r - retLo) / (retHi - retLo || 1)) * (H - padT - padB);
  const plotL = padL, plotR = W - padR, plotT = padT, plotB = H - padB;

  // ── Frontier, split at the Min Variance point ──
  // Only the upper branch is the efficient frontier; the lower branch (same
  // risk, less return) is drawn faint so it isn't read as frontier.
  const fp = data.frontier_points;
  let effD = '', ineffD = '';
  let eff: EfficientFrontierPoint[] = [], ineff: EfficientFrontierPoint[] = [];
  if (fp.length > 1) {
    const iMin = fp.reduce((best, p, i) => (p.risk < fp[best].risk ? i : best), 0);
    const minRet = fp[iMin].return;
    eff = fp.filter((p) => p.return >= minRet).sort((p, q) => p.return - q.return);
    ineff = fp.filter((p) => p.return <= minRet).sort((p, q) => q.return - p.return);
    const toD = (pts: EfficientFrontierPoint[]) =>
      pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.risk).toFixed(1)} ${y(p.return).toFixed(1)}`).join(' ');
    effD = eff.length > 1 ? toD(eff) : '';
    ineffD = ineff.length > 1 ? toD(ineff) : '';
  }

  // ── Capital Market Line, as a tangent segment at Max Sharpe ──
  // The full line runs from the risk-free rate at 0% risk. For a high-vol
  // basket that ray crossed the whole chart through empty space and forced the
  // axis to 0. What it's for is showing that Max Sharpe is where a line from
  // the risk-free rate just touches the frontier — a short segment through
  // that point shows exactly that.
  const cmlSeg = ms && ms.risk > 0 && ms.return > rf && W > 0
    ? (() => {
        const slope = (ms.return - rf) / ms.risk;
        const cx = x(ms.risk), cy = y(ms.return);
        const dx = x(ms.risk + 1) - cx, dy = y(ms.return + slope) - cy;
        const len = Math.hypot(dx, dy) || 1;
        const L = (plotR - plotL) * 0.13;
        return { x1: cx - (dx / len) * L, y1: cy - (dy / len) * L, x2: cx + (dx / len) * L, y2: cy + (dy / len) * L };
      })()
    : null;
  const rfVisible = riskLo === 0 && rf >= retLo && rf <= retHi;

  // ── Edge arrows for anything outside the view ──
  type Edge = { key: string; text: string; tip: string; cx: number; cy: number; angle: number; held: boolean; bench: boolean };
  const edgeOf = (risk: number, ret: number) => {
    const right = risk > riskHi, left = risk < riskLo, up = ret > retHi, down = ret < retLo;
    const cx = Math.min(Math.max(x(risk), plotL + 8), plotR - 8);
    const cy = Math.min(Math.max(y(ret), plotT + 8), plotB - 8);
    const angle = right ? (up ? -45 : down ? 45 : 0) : left ? (up ? -135 : down ? 135 : 180) : up ? -90 : down ? 90 : 0;
    return { cx, cy, angle };
  };

  // ── Label placement ──
  // Each label tries two rings of positions around its point and takes the
  // first that clears every label already placed, every marker, and the
  // lines. Optional labels are dropped rather than overlapped; the rest fall
  // back to above their point. Placement ignores hover and selection so
  // labels never jump when either changes.
  const FS = 11;
  type Rect = { x0: number; y0: number; x1: number; y1: number };
  type Label = { x: number; y: number; anchor: 'start' | 'middle' | 'end' };
  const hit = (p: Rect, q: Rect) => p.x0 < q.x1 && q.x0 < p.x1 && p.y0 < q.y1 && q.y0 < p.y1;
  const box = (cx: number, cy: number, r: number): Rect => ({ x0: cx - r, y0: cy - r, x1: cx + r, y1: cy + r });

  const rfInfo = data.risk_free;
  const rfTooltip = rfInfo
    ? `Risk-free rate ${rfInfo.rate.toFixed(2)}% — ${rfInfo.tenor}${rfInfo.as_of ? `, as of ${rfInfo.as_of}` : ''}. Source: ${rfInfo.source}.` +
      (rfInfo.live ? '' : ' No live short-rate feed exists for this market, so this is a static estimate.')
    : `Risk-free rate ${rf.toFixed(2)}%`;
  const assetTooltip = (a: IndividualAsset) => {
    const lines = [
      `${a.symbol}${a.name && a.name !== a.symbol ? ` — ${a.name}` : ''}`,
      `Plotted return ${a.return.toFixed(1)}% (${returnDef(data)})`,
    ];
    if (a.compound_return != null) lines.push(`Actual compound return ${a.compound_return.toFixed(1)}% a year`);
    lines.push(`Volatility ${a.risk.toFixed(1)}%`);
    const from = data.fx_converted?.[a.symbol];
    if (from) lines.push(`Trades in ${from}; converted to ${data.currency} at daily rates`);
    if (data.fx_failed?.includes(a.symbol)) lines.push(`No exchange rate available: figures are in its own currency, not ${data.currency}`);
    return lines.join('\n');
  };
  const benchText = (b: BenchmarkAsset) =>
    isPortfolio(b) ? 'Your portfolio'
      : b.tier === 'secondary' && b.currency && !b.converted_from && b.currency !== (data.currency || data.base_currency) ? `${b.name} · ${b.currency}`
      : b.name;
  const benchTooltip = (b: BenchmarkAsset) =>
    `${benchText(b)}${b.symbol !== 'USER_PORTFOLIO' ? ` (${b.symbol})` : ''}\nReturn ${b.return.toFixed(1)}% · Volatility ${b.risk.toFixed(1)}% · Sharpe ${b.sharpe.toFixed(2)}`;

  const edges: Edge[] = W > 0 ? [
    ...offStocks.map((a) => ({ key: `s-${a.symbol}`, text: a.symbol, tip: assetTooltip(a), held: heldSymbols.has(a.symbol), bench: false, ...edgeOf(a.risk, a.return) })),
    ...offBench.map((b) => ({ key: `b-${b.symbol}`, text: benchText(b), tip: benchTooltip(b), held: true, bench: true, ...edgeOf(b.risk, b.return) })),
  ] : [];

  const markers: Rect[] = [
    ...inView.map((a) => box(x(a.risk), y(a.return), 5)),
    ...strategyPoints.map((s) => box(x(s.point!.risk), y(s.point!.return), 6)),
    ...[...benchInView, ...portfolioPts].map((b) => box(x(b.risk), y(b.return), 7)),
    ...edges.map((e) => box(e.cx, e.cy, 6)),
    ...(rfVisible ? [box(x(0), y(rf), 4)] : []),
  ];
  const lineObstacles = (pts: [number, number][]) => {
    for (let i = 0; i < pts.length - 1; i++) {
      const [x0, y0] = pts[i], [x1, y1] = pts[i + 1];
      const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 10));
      for (let k = 0; k <= steps; k++) markers.push(box(x0 + ((x1 - x0) * k) / steps, y0 + ((y1 - y0) * k) / steps, 2.5));
    }
  };
  if (W > 0) {
    lineObstacles(eff.map((p) => [x(p.risk), y(p.return)]));
    lineObstacles(ineff.map((p) => [x(p.risk), y(p.return)]));
    if (cmlSeg) lineObstacles([[cmlSeg.x1, cmlSeg.y1], [cmlSeg.x2, cmlSeg.y2]]);
  }

  const placed: Rect[] = [];
  const place = (px: number, py: number, text: string, r: number, fs = FS, optional = false): Label | null => {
    const w = text.length * fs * 0.62; // monospace advance
    const ring = (g: number): Label[] => [
      { x: px, y: py - r - g, anchor: 'middle' },                  // above
      { x: px + r + g + 1, y: py + fs * 0.35, anchor: 'start' },   // right
      { x: px, y: py + r + fs + g - 2, anchor: 'middle' },         // below
      { x: px - r - g - 1, y: py + fs * 0.35, anchor: 'end' },     // left
      { x: px + r + g - 1, y: py - r - g + 1, anchor: 'start' },   // above-right
      { x: px - r - g + 1, y: py - r - g + 1, anchor: 'end' },     // above-left
      { x: px + r + g - 1, y: py + r + fs + g - 3, anchor: 'start' }, // below-right
      { x: px - r - g + 1, y: py + r + fs + g - 3, anchor: 'end' },   // below-left
    ];
    const boxOf = (c: Label): Rect => {
      const x0 = c.anchor === 'middle' ? c.x - w / 2 : c.anchor === 'start' ? c.x : c.x - w;
      return { x0, y0: c.y - fs + 1, x1: x0 + w, y1: c.y + 2 };
    };
    for (const c of [...ring(4), ...ring(14)]) {
      const bx = boxOf(c);
      if (bx.x0 < plotL - 2 || bx.x1 > W - 2 || bx.y0 < 2 || bx.y1 > plotB + 4) continue;
      if (placed.some((q) => hit(q, bx)) || markers.some((m) => hit(m, bx))) continue;
      placed.push(bx);
      return c;
    }
    if (optional) return null;
    const c = ring(4)[0];
    placed.push(boxOf(c));   // nowhere clear: overlap above rather than lose it
    return c;
  };
  const initialsOf = (key: string) =>
    STRATEGY_DEFS.find((d) => d.key === key)!.label.split(' ').map((w) => w[0]).join('');
  const stratLabels: Record<string, Label | null> = {};
  const benchLabels: Record<string, Label | null> = {};
  const assetLabels: Record<string, Label | null> = {};
  const edgeLabels: Record<string, Label | null> = {};
  let rfLabel: Label | null = null;
  if (W > 0) {
    // Priority order: what matters most gets first pick of the clear spots.
    strategyPoints.forEach((s) => {
      stratLabels[s.key as string] = place(x(s.point!.risk), y(s.point!.return), initialsOf(s.key as string), 7);
    });
    portfolioPts.forEach((b) => { benchLabels[b.symbol] = place(x(b.risk), y(b.return), benchText(b), 7); });
    [...benchInView].sort((p, q) => Number(p.tier === 'secondary') - Number(q.tier === 'secondary'))
      .forEach((b) => { benchLabels[b.symbol] = place(x(b.risk), y(b.return), benchText(b), 7, b.tier === 'secondary' ? 10 : FS, b.tier === 'secondary'); });
    inView.forEach((a) => { assetLabels[a.symbol] = place(x(a.risk), y(a.return), a.symbol, 5); });
    edges.forEach((e) => { edgeLabels[e.key] = place(e.cx, e.cy, e.text, 6, e.bench ? 10 : FS, e.bench); });
    if (rfVisible) rfLabel = place(x(0), y(rf), `Rf ${rf.toFixed(1)}%`, 4, 10, true);
  }

  // Hovered stock drawn last so it sits on top of anything it overlaps.
  const assetsInOrder = [...inView].sort(
    (p, q) => Number(p.symbol === hoveredSymbol) - Number(q.symbol === hoveredSymbol),
  );
  const msColor = STRATEGY_DEFS.find((d) => d.needsForecast)?.color || '#f44336';
  const outsideCount = offStocks.length + offBench.length;

  return (
    <Box ref={wrapRef} sx={{ bgcolor: 'var(--bb-bg-primary)', borderRadius: 1, minHeight: 320, position: 'relative' }}>
      {anyOutside && (
        <Box sx={{
          position: 'absolute', top: 4, right: padR, zIndex: 1,
          display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, color: 'var(--bb-text-muted)',
        }}>
          {!showAll && outsideCount > 0 && (
            <span>
              {outsideCount} outside this view ({[...offStocks.map((a) => a.symbol), ...offBench.map((b) => b.name)].join(', ')})
            </span>
          )}
          <Button size="small" variant="outlined" onClick={() => setShowAll((v) => !v)}
            sx={{ py: 0, px: 1, minWidth: 0, fontSize: 11, textTransform: 'none', lineHeight: 1.8 }}>
            {showAll ? 'Fit to frontier' : 'Show everything'}
          </Button>
        </Box>
      )}
      {W > 0 && (
      <svg width={W} height={H} style={{ display: 'block' }}>
        <defs>
          <clipPath id={clipId}>
            <rect x={plotL} y={plotT} width={plotR - plotL} height={plotB - plotT} />
          </clipPath>
        </defs>

        {ticks(riskLo, riskHi, riskStep).map((rv) => (
          <g key={`gx-${rv}`}>
            <line x1={x(rv)} y1={plotT} x2={x(rv)} y2={plotB} stroke="var(--bb-chart-grid)" strokeWidth={1} />
            <text x={x(rv)} y={plotB + 17} fontSize={10.5} fill="var(--bb-text-muted)" textAnchor="middle" fontFamily="monospace">{fmtTick(rv, riskStep)}</text>
          </g>
        ))}
        {ticks(retLo, retHi, retStep).map((rv) => (
          <g key={`gy-${rv}`}>
            <line x1={plotL} y1={y(rv)} x2={plotR} y2={y(rv)}
              stroke={rv === 0 ? 'var(--bb-border-secondary)' : 'var(--bb-chart-grid)'} strokeWidth={1} />
            <text x={plotL - 8} y={y(rv) + 4} fontSize={10.5} fill="var(--bb-text-muted)" textAnchor="end" fontFamily="monospace">{fmtTick(rv, retStep)}</text>
          </g>
        ))}
        <text x={(plotL + plotR) / 2} y={H - 6} fontSize={10} fill="var(--bb-text-muted)" textAnchor="middle" letterSpacing="0.08em" fontFamily="monospace">
          RISK · ANNUALISED VOLATILITY{ccyTag}, TRAILING {period} →
        </text>
        <text x={16} y={(plotT + plotB) / 2} fontSize={10} fill="var(--bb-text-muted)" textAnchor="middle" letterSpacing="0.08em" fontFamily="monospace" transform={`rotate(-90 16 ${(plotT + plotB) / 2})`}>
          RETURN · {returnDef(data).toUpperCase().replace(' RETURN', '')}{ccyTag}, TRAILING {period} →
        </text>

        <g clipPath={`url(#${clipId})`}>
          {ineffD && <path d={ineffD} fill="none" stroke="var(--bb-text-muted)" strokeWidth={1.25} strokeDasharray="4 4" opacity={0.55} />}
          {effD && <path d={effD} fill="none" stroke="var(--bb-text-primary)" strokeWidth={2.25} opacity={0.85} strokeLinejoin="round" />}
          {cmlSeg && (
            <line x1={cmlSeg.x1} y1={cmlSeg.y1} x2={cmlSeg.x2} y2={cmlSeg.y2}
              stroke={msColor} strokeWidth={1.5} strokeDasharray="6 4" opacity={0.75}>
              <title>{`Capital Market Line: from the risk-free rate (${rf.toFixed(2)}%) through Max Sharpe, the point where it just touches the frontier.`}</title>
            </line>
          )}
        </g>

        {rfVisible && (
          <g>
            <title>{rfTooltip}</title>
            <circle cx={x(0)} cy={y(rf)} r={3.5} fill={rfInfo && !rfInfo.live ? WARN : 'var(--bb-text-secondary)'} />
            {rfLabel && (
              <text x={rfLabel.x} y={rfLabel.y} fontSize={10} fill={rfInfo && !rfInfo.live ? WARN : 'var(--bb-text-secondary)'}
                textAnchor={rfLabel.anchor} fontFamily="monospace">
                Rf {rf.toFixed(1)}%
              </text>
            )}
          </g>
        )}

        {assetsInOrder.map((a) => {
          const hot = a.symbol === hoveredSymbol;
          const held = heldSymbols.has(a.symbol);
          const half = hot ? 6 : 4;
          const lb = assetLabels[a.symbol];
          return (
            <g key={a.symbol} opacity={hot || held ? 1 : 0.3} style={{ transition: 'opacity .15s' }}>
              <title>{assetTooltip(a)}</title>
              <rect
                x={x(a.risk) - half} y={y(a.return) - half} width={half * 2} height={half * 2}
                fill={hot ? accent : 'var(--bb-bg-primary)'}
                stroke={hot ? accent : 'var(--bb-text-secondary)'} strokeWidth={1.6}
                transform={`rotate(45 ${x(a.risk)} ${y(a.return)})`}
              />
              {lb && (
                <text x={lb.x} y={lb.y} fontSize={hot ? FS + 1 : FS} fontWeight={700} textAnchor={lb.anchor}
                  fill={hot ? accent : 'var(--bb-text-secondary)'} fontFamily="monospace">
                  {a.symbol}
                </text>
              )}
            </g>
          );
        })}

        {/* Outside the view: arrows on the border pointing to where they really are. */}
        {edges.map((e) => {
          const lb = edgeLabels[e.key];
          return (
            <g key={e.key} opacity={e.held ? (e.bench ? 0.7 : 0.9) : 0.35} style={{ cursor: 'pointer' }} onClick={() => setShowAll(true)}>
              <title>{e.tip + '\nOutside this view — click to show everything.'}</title>
              <path d={`M ${e.cx - 5} ${e.cy - 5} L ${e.cx + 5} ${e.cy} L ${e.cx - 5} ${e.cy + 5} Z`}
                fill={e.bench ? 'none' : 'var(--bb-text-secondary)'} stroke="var(--bb-text-secondary)" strokeWidth={e.bench ? 1.4 : 0}
                transform={`rotate(${e.angle} ${e.cx} ${e.cy})`} />
              {lb && (
                <text x={lb.x} y={lb.y} fontSize={e.bench ? 10 : FS} fontWeight={e.bench ? 400 : 700} textAnchor={lb.anchor}
                  fill={e.bench ? 'var(--bb-text-muted)' : 'var(--bb-text-secondary)'} fontFamily="monospace">
                  {e.text}
                </text>
              )}
            </g>
          );
        })}

        {/* Benchmarks and your portfolio: where you stand against the frontier. */}
        {[...benchInView, ...portfolioPts].map((b) => {
          const cx = x(b.risk), cy = y(b.return);
          const lb = benchLabels[b.symbol];
          const secondary = b.tier === 'secondary';
          return (
            <g key={`b-${b.symbol}`} opacity={secondary ? 0.6 : 1}>
              <title>{benchTooltip(b)}</title>
              {isPortfolio(b) ? (
                <>
                  <circle cx={cx} cy={cy} r={7} fill="none" stroke="var(--bb-text-primary)" strokeWidth={2} />
                  <circle cx={cx} cy={cy} r={2.2} fill="var(--bb-text-primary)" />
                </>
              ) : (
                <rect x={cx - 5} y={cy - 5} width={10} height={10} rx={1.5}
                  fill={secondary ? 'var(--bb-bg-primary)' : 'var(--bb-text-primary)'}
                  stroke={secondary ? 'var(--bb-text-muted)' : 'var(--bb-text-primary)'} strokeWidth={1.5} />
              )}
              {lb && (
                <text x={lb.x} y={lb.y} fontSize={secondary ? 10 : FS} fontWeight={secondary ? 400 : 700}
                  fill={secondary ? 'var(--bb-text-muted)' : 'var(--bb-text-primary)'} textAnchor={lb.anchor} fontFamily="monospace">
                  {benchText(b)}
                </text>
              )}
            </g>
          );
        })}

        {strategyPoints.map(({ key, color, point }) => {
          const sel = key === selectedKey;
          const lb = stratLabels[key as string];
          return (
            <g key={key} onClick={() => onSelectKey(key as string)} style={{ cursor: 'pointer' }}>
              {sel && <circle cx={x(point!.risk)} cy={y(point!.return)} r={11} fill={color} opacity={0.2} />}
              <circle cx={x(point!.risk)} cy={y(point!.return)} r={sel ? 6.5 : 5} fill={color} stroke="var(--bb-bg-primary)" strokeWidth={2} />
              {lb && (
                <text x={lb.x} y={lb.y} fontSize={FS} fontWeight={700} fill={color} textAnchor={lb.anchor} fontFamily="monospace">
                  {initialsOf(key as string)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      )}
    </Box>
  );
};

// How the plotted return is defined, which depends on the sampling frequency.
const returnDef = (data: EfficientFrontierResponse) =>
  data.sample?.frequency === 'weekly' ? 'mean weekly return × 52' : 'mean daily return × 252';

const IN_SAMPLE_NOTE =
  'In-sample: each strategy\'s weights were fitted to the same trailing period these figures ' +
  'measure, so they describe how that allocation would have done, not how it will do. ' +
  'Long-run Sharpe ratios for equities are typically around 0.3–0.5.';

// Legend drawn with the same glyphs the chart uses, so each mark can be
// matched by eye rather than decoded from a paragraph.
const ChartLegend: React.FC<{ data: EfficientFrontierResponse }> = ({ data }) => {
  const primaries = (data.benchmarks || []).filter((b) => b.tier === 'primary');
  const hasSecondary = (data.benchmarks || []).some((b) => b.tier === 'secondary');
  const hasPortfolio = (data.benchmarks || []).some((b) => b.tier === 'portfolio' || b.symbol === 'USER_PORTFOLIO');
  const msColor = STRATEGY_DEFS.find((d) => d.needsForecast)?.color || '#f44336';
  const G = (children: React.ReactNode) => (
    <svg width={22} height={12} style={{ flexShrink: 0, overflow: 'visible' }}>{children}</svg>
  );
  const items: [React.ReactNode, string][] = [
    [G(<line x1={1} y1={6} x2={21} y2={6} stroke="var(--bb-text-primary)" strokeWidth={2.25} opacity={0.85} />), 'Efficient frontier'],
    [G(<line x1={1} y1={6} x2={21} y2={6} stroke="var(--bb-text-muted)" strokeWidth={1.25} strokeDasharray="4 4" opacity={0.7} />), 'Inefficient half'],
    [G(<line x1={1} y1={6} x2={21} y2={6} stroke={msColor} strokeWidth={1.25} strokeDasharray="6 4" opacity={0.75} />), `Capital Market Line — tangent from the risk-free rate (${(data.risk_free?.rate ?? data.risk_free_rate).toFixed(1)}%), touching at Max Sharpe`],
    [G(<circle cx={11} cy={6} r={4.5} fill={STRATEGY_DEFS[0].color} />), 'Strategies'],
    [G(<rect x={7} y={2} width={8} height={8} fill="none" stroke="var(--bb-text-secondary)" strokeWidth={1.5} transform="rotate(45 11 6)" />), 'Stocks (faded = not held)'],
  ];
  if (primaries.length) items.push([G(<rect x={6} y={1} width={10} height={10} rx={1.5} fill="var(--bb-text-primary)" />), `${primaries.map((b) => b.name).join(', ')} (local benchmark)`]);
  if (hasSecondary) items.push([G(<rect x={6} y={1} width={10} height={10} rx={1.5} fill="none" stroke="var(--bb-text-muted)" strokeWidth={1.5} />), 'Global benchmarks']);
  if (hasPortfolio) items.push([G(<><circle cx={11} cy={6} r={5.5} fill="none" stroke="var(--bb-text-primary)" strokeWidth={2} /><circle cx={11} cy={6} r={1.8} fill="var(--bb-text-primary)" /></>), 'Your portfolio']);
  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.75 }}>
        {items.map(([glyph, text]) => (
          <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {glyph}
            <Typography sx={{ fontSize: 11.5, color: 'var(--bb-text-secondary)' }}>{text}</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 0.75, fontSize: 11, color: 'var(--bb-text-muted)', fontStyle: 'italic' }}>
        All figures are in {data.currency || 'local currency'}, trailing {(data.period || '1y').toUpperCase()}
        {data.fx_converted && Object.keys(data.fx_converted).length > 0
          ? ` (${Object.keys(data.fx_converted).join(', ')} converted at daily exchange rates)` : ''}. Returns are the {returnDef(data)} —
        the optimiser's input; for very volatile stocks it can differ sharply from what the stock actually
        compounded to, which is shown in the list below (hover a stock for both). Strategy returns and Sharpe
        ratios are in-sample: the weights were fitted to the same data they are measured on, so they describe
        the past year rather than forecast the next.
      </Typography>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Ranked holdings for the selected strategy. Answers "what does this buy, and
// how much" top to bottom, and adds one column the old colour bars could not
// show legibly: how many of the forecast-free strategies agree on each stock.
// ──────────────────────────────────────────────────────────────────────────
const HoldingsList: React.FC<{
  data: EfficientFrontierResponse;
  selectedKey: string;
  onHoverSymbol: (symbol: string | null) => void;
}> = ({ data, selectedKey, onHoverSymbol }) => {
  const [expanded, setExpanded] = useState(false);
  // Each strategy starts folded; expanding one shouldn't carry to the next.
  useEffect(() => { setExpanded(false); }, [selectedKey]);
  const def = STRATEGY_DEFS.find((d) => d.key === selectedKey);
  const point = data[selectedKey as keyof EfficientFrontierResponse] as EfficientFrontierPoint | null | undefined;
  if (!def || !point) return null;

  const assetBySym: Record<string, IndividualAsset> = {};
  data.individual_assets.forEach((a) => { assetBySym[a.symbol] = a; });
  // The backend falls back to the ticker when it has no name; don't print it twice.
  const names: Record<string, string> = {};
  data.individual_assets.forEach((a) => {
    if (a.name && a.name !== a.symbol) names[a.symbol] = a.name;
  });

  const robust = ROBUST_DEFS
    .map((d) => ({ label: d.label, pt: data[d.key] as EfficientFrontierPoint | null | undefined }))
    .filter((r): r is { label: string; pt: EfficientFrontierPoint } => !!r.pt);

  const rangeOf = (sym: string) => {
    const ws = robust.map((r) => r.pt.weights[sym] || 0);
    return ws.length ? { lo: Math.min(...ws), hi: Math.max(...ws) } : null;
  };
  const pct = (w: number) => `${(w * 100).toFixed(w >= 0.1 ? 0 : 1)}%`;
  const rangeText = (r: { lo: number; hi: number }) =>
    Math.abs(r.hi - r.lo) < 0.005 ? pct(r.lo) : `${pct(r.lo)}–${pct(r.hi)}`;

  const rows = Object.entries(point.weights)
    .filter(([, w]) => w >= SHOW_MIN)
    .sort(([, a], [, b]) => b - a);
  const shown = new Set(rows.map(([sym]) => sym));
  const notHeld = data.individual_assets.map((a) => a.symbol).filter((sym) => !shown.has(sym));

  let headCount = rows.length;
  {
    let cum = 0;
    for (let i = 0; i < rows.length; i++) {
      cum += rows[i][1];
      if (i + 1 >= COLLAPSE_MIN_ROWS && cum >= COLLAPSE_COVER) { headCount = i + 1; break; }
    }
  }
  const tail = rows.slice(headCount);
  const foldable = tail.length >= COLLAPSE_MIN_TAIL;
  const visible = foldable && !expanded ? rows.slice(0, headCount) : rows;
  const tailWeight = tail.reduce((acc, [, w]) => acc + w, 0);

  // Weight bars and range strips share one scale: the position cap, so a full
  // bar reads as "at the cap". Uncapped, fall back to the largest holding.
  const cap = data.estimation?.max_weight;
  const largest = rows.length ? rows[0][1] : 1;
  const capped = cap !== undefined && cap < 1;
  const scale = capped ? Math.max(cap!, largest) : largest;
  const at = (w: number) => `${Math.min(100, (w / scale) * 100)}%`;

  // One line per row. Phones drop the weight bar and the range strip (the
  // range stays as text) so a row still fits on one line at ~400px.
  // At full page width the spare space is shared between the weight bar and
  // the range strip — both on the same 0-to-cap scale, so a wider strip is
  // finer resolution, not padding.
  const GRID = {
    xs: 'minmax(0, 1fr) 54px 92px',
    sm: 'minmax(180px, 1.2fr) minmax(80px, 2fr) 58px 76px minmax(196px, 1.2fr)',
  };
  const rangeHelp =
    `The spread of weights the ${robust.length} forecast-free strategies give this stock (grey band), ` +
    `with the selected strategy marked. A mark outside the band means this strategy disagrees with every ` +
    `risk-based method about how much to hold — typical of a pick driven by the return forecast.`;

  return (
    <Box>
      <Box sx={{
        display: 'grid', gridTemplateColumns: GRID, gap: 1.25, px: 1, pb: 0.75,
        borderBottom: '1px solid var(--bb-border-secondary)',
      }}>
        {['Holding', 'Weight', '', 'Return', 'Other methods'].map((h, i) => (
          <Typography key={i} sx={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--bb-text-muted)', textAlign: i >= 2 ? 'right' : 'left',
            display: i === 1 || i === 3 ? { xs: 'none', sm: 'block' } : 'block',
          }}>
            {i === 3 ? (
              <Tooltip title={`Each stock's actual compound return over the trailing ${(data.period || '1y').toUpperCase()}, annualised — what holding it really did. Amber where it differs by ${RETURN_GAP_FLAG}+ points from the figure the chart plots (${returnDef(data)}).`}>
                <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>{h}</span>
              </Tooltip>
            ) : i === 4 ? (
              <Tooltip title={rangeHelp}>
                <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>{h}</span>
              </Tooltip>
            ) : h}
          </Typography>
        ))}
      </Box>

      {visible.map(([sym, w]) => {
        const r = rangeOf(sym);
        // A forecast-free strategy is one of the five, so it sits inside the
        // band by construction; only a forecast-driven one can fall outside.
        const side = !r || !def.needsForecast ? null
          : w > r.hi + RANGE_TOL ? 'above'
          : w < r.lo - RANGE_TOL ? 'below'
          : null;
        return (
          <Box
            key={sym}
            onMouseEnter={() => onHoverSymbol(sym)}
            onMouseLeave={() => onHoverSymbol(null)}
            sx={{
              display: 'grid', gridTemplateColumns: GRID, gap: 1.25, alignItems: 'center',
              px: 1, py: 0.6, borderBottom: '1px solid var(--bb-border-primary)',
              '&:hover': { bgcolor: 'var(--bb-bg-hover)' },
            }}
          >
            {/* Ticker and name share one line; the name truncates first. */}
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>
                {sym}
              </Typography>
              {data.fx_converted?.[sym] && (
                <Tooltip title={`Trades in ${data.fx_converted[sym]}; converted to ${data.currency} at daily rates, so its risk and return include the currency's moves — as they would for a ${data.currency} investor.`}>
                  <Typography sx={{
                    fontSize: 9.5, fontWeight: 700, fontFamily: 'monospace', px: 0.5, borderRadius: 0.5, flexShrink: 0,
                    border: '1px solid var(--bb-border-secondary)', color: 'var(--bb-text-muted)', cursor: 'help', alignSelf: 'center',
                  }}>
                    {data.fx_converted[sym]}
                  </Typography>
                </Tooltip>
              )}
              {names[sym] && (
                <Typography noWrap title={names[sym]} sx={{ fontSize: 12, color: 'var(--bb-text-muted)', minWidth: 0 }}>
                  {names[sym]}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'block' }, height: 8, borderRadius: 4, bgcolor: 'var(--bb-bg-tertiary)', overflow: 'hidden' }}>
              <Box sx={{ width: at(w), height: '100%', bgcolor: def.color, borderRadius: 4, transition: 'width .25s' }} />
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', textAlign: 'right' }}>
              {(w * 100).toFixed(1)}%
            </Typography>

            {(() => {
              const a = assetBySym[sym];
              const cr = a?.compound_return;
              if (!a || cr == null) return <Box sx={{ display: { xs: 'none', sm: 'block' } }} />;
              const gap = a.return - cr;
              const flag = Math.abs(gap) >= RETURN_GAP_FLAG;
              return (
                <Tooltip title={
                  `${sym} actually compounded ${cr >= 0 ? '+' : ''}${cr.toFixed(1)}% a year. The chart plots ` +
                  `${a.return >= 0 ? '+' : ''}${a.return.toFixed(1)}% (${returnDef(data)}, the optimiser's input). ` +
                  (flag
                    ? `A ${Math.abs(gap).toFixed(0)}-point gap: at ${a.risk.toFixed(0)}% volatility the two measures diverge a lot, so read its position on the chart with care.`
                    : 'The two agree closely for this stock.')
                }>
                  <Typography sx={{
                    display: { xs: 'none', sm: 'block' }, fontSize: 12, fontFamily: 'monospace', textAlign: 'right', cursor: 'help',
                    color: flag ? WARN : cr >= 0 ? 'var(--bb-text-secondary)' : 'var(--bb-text-muted)', fontWeight: flag ? 700 : 400,
                  }}>
                    {cr >= 0 ? '+' : ''}{cr.toFixed(0)}%{flag ? ' ≠' : ''}
                  </Typography>
                </Tooltip>
              );
            })()}

            {r ? (
              <Tooltip title={
                <Box sx={{ fontSize: 11.5, lineHeight: 1.55 }}>
                  <Box sx={{ fontWeight: 700, mb: 0.5 }}>{sym} across the forecast-free strategies</Box>
                  {robust.map((rb) => {
                    const rw = rb.pt.weights[sym] || 0;
                    return (
                      <Box key={rb.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <span>{rb.label}</span>
                        <span style={{ fontFamily: 'monospace' }}>{rw >= SHOW_MIN ? `${(rw * 100).toFixed(1)}%` : '—'}</span>
                      </Box>
                    );
                  })}
                  {side && (
                    <Box sx={{ mt: 0.75, color: WARN }}>
                      {def.label} holds {(w * 100).toFixed(1)}% — {side} what any risk-based method gives it
                      ({rangeText(r)}). That gap comes from the return forecast, so treat it as the least reliable part of this allocation.
                    </Box>
                  )}
                </Box>
              }>
                <Box sx={{ cursor: 'help', display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end', width: '100%' }}>
                  <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'relative', height: 12, flex: 1, minWidth: 60 }}>
                    <Box sx={{ position: 'absolute', left: 0, right: 0, top: 4, height: 4, borderRadius: 2, bgcolor: 'var(--bb-bg-tertiary)' }} />
                    <Box sx={{
                      position: 'absolute', top: 4, height: 4, borderRadius: 2,
                      left: at(r.lo), width: `max(3px, calc(${at(r.hi)} - ${at(r.lo)}))`,
                      bgcolor: 'var(--bb-text-muted)', opacity: 0.55,
                    }} />
                    <Box sx={{
                      position: 'absolute', top: 0, width: 2, height: 12, borderRadius: 1,
                      left: `calc(${at(w)} - 1px)`, bgcolor: side ? WARN : def.color,
                    }} />
                  </Box>
                  <Typography noWrap sx={{
                    fontSize: 10.5, fontFamily: 'monospace', textAlign: 'right', minWidth: 0,
                    width: { xs: 'auto', sm: 96 }, flexShrink: 0,
                    color: side ? WARN : 'var(--bb-text-muted)', fontWeight: side ? 700 : 400,
                  }}>
                    {side === 'above' ? '▲ ' : side === 'below' ? '▼ ' : ''}{rangeText(r)}
                  </Typography>
                </Box>
              </Tooltip>
            ) : <Box />}
          </Box>
        );
      })}

      {foldable && (
        <Box
          component="button"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            all: 'unset', boxSizing: 'border-box', width: '100%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.8,
            borderBottom: '1px solid var(--bb-border-primary)',
            color: 'var(--bb-text-secondary)', fontSize: 12,
            '&:hover': { bgcolor: 'var(--bb-bg-hover)', color: 'var(--bb-text-primary)' },
            '&:focus-visible': { outline: `2px solid ${def.color}`, outlineOffset: -2 },
          }}
        >
          {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          {expanded
            ? 'Show fewer'
            : <>+ {tail.length} smaller positions <span style={{ color: 'var(--bb-text-muted)' }}>· {(tailWeight * 100).toFixed(1)}% combined</span></>}
        </Box>
      )}

      <Box sx={{ mt: 1, px: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {notHeld.length > 0 && (
          <Typography sx={{ fontSize: 11, color: 'var(--bb-text-muted)' }}>
            Not held by {def.label}:{' '}
            {notHeld.map((sym, i) => {
              const r = rangeOf(sym);
              // Dropping a stock every risk-based method holds meaningfully is
              // the same forecast-driven disagreement as over-weighting one.
              const dropped = def.needsForecast && r && r.lo >= RANGE_TOL;
              return (
                <span key={sym}>
                  {i > 0 && ', '}
                  <span style={{ fontFamily: 'monospace', color: dropped ? WARN : undefined, fontWeight: dropped ? 700 : 400 }}>
                    {sym}
                  </span>
                  {r && r.hi >= SHOW_MIN && <span> (others {rangeText(r)})</span>}
                </span>
              );
            })}
          </Typography>
        )}
        <Typography sx={{ fontSize: 10.5, color: 'var(--bb-text-muted)', fontStyle: 'italic' }}>
          {capped
            ? `Bars are scaled to the ${(cap! * 100).toFixed(0)}% position cap — a full bar is a position at the cap.`
            : 'Bars are scaled to the largest holding.'}
          {def.needsForecast && ' Amber marks where this strategy departs from every forecast-free method.'}
        </Typography>
      </Box>
    </Box>
  );
};

const EfficientFrontier: React.FC<EfficientFrontierProps> = ({ symbols: propSymbols, userWeights, onReset, onApplyWeights, jobLabel, jobRoute }) => {
  // The calculation runs as a background job (services/backgroundJobs): it keeps
  // going if the user leaves, a pop-up says when it's ready, and coming back
  // shows the stored result. Only the latest request may update this view.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const latestKey = useRef<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => watchJob(activeKey), [activeKey]);
  const [data, setData] = useState<EfficientFrontierResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('1y');
  const [selectedKey, setSelectedKey] = useState<string>('max_sharpe_portfolio');
  // Row under the pointer in the holdings list, mirrored as a highlight on the chart.
  const [hoveredSymbol, setHoveredSymbol] = useState<string | null>(null);
  const [calculatedSymbols, setCalculatedSymbols] = useState<string[]>([]);
  const [rfCountry, setRfCountry] = useState<string>('AUTO');
  // Per-asset ceiling, as a percentage for the slider. Applied on release
  // rather than on drag — each change re-runs six optimisations plus a
  // 100-point frontier, so live-updating would queue a request per pixel.
  const [maxWeightPct, setMaxWeightPct] = useState<number>(25);
  // Mirrors the slider while dragging so the label tracks the thumb without
  // firing a request on every pixel.
  const [pendingCapPct, setPendingCapPct] = useState<number>(25);
  const [rfRates, setRfRates] = useState<RfRateOption[]>([]);
  const [infoOpen, setInfoOpen] = useState(false);

  // Fetch available risk-free rate options once on mount
  useEffect(() => {
    const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
    fetch(`${API_BASE_URL}/api/analytics/risk-free-rates`)
      .then(r => r.json())
      .then(d => setRfRates(d.rates || []))
      .catch(() => {});
  }, []);

  // No fully-invested long-only portfolio can hold n assets all below 1/n, so
  // the slider's floor moves with the number of symbols. Rounded up so the
  // chosen value is always actually feasible.
  //
  // Same symbol source the fetch uses. Computed every render, not memoised: in
  // standalone mode the list lives in localStorage, which a dependency array
  // cannot observe. (This previously read an undeclared `tickers` and threw
  // whenever the component rendered without symbols passed in.)
  const capSymbolCount = ((propSymbols && propSymbols.length >= 2) ? propSymbols : loadTickers()).length;
  const capFloorPct = capSymbolCount > 0 ? Math.ceil(100 / capSymbolCount) : 1;

  // Keep the cap at or above the floor when symbols are removed.
  useEffect(() => {
    if (maxWeightPct < capFloorPct) {
      setMaxWeightPct(capFloorPct);
      setPendingCapPct(capFloorPct);
    }
  }, [capFloorPct, maxWeightPct]);

  const fetchFrontier = useCallback(async (tickersToUse: string[]) => {
    if (!tickersToUse || tickersToUse.length < 2) {
      setError('At least 2 tickers required for efficient frontier');
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get user's actual portfolio info — use explicit userWeights if provided (model portfolios)
      const portfolioSymbols = userWeights ? Object.keys(userWeights) : getPortfolioSymbols();
      const portfolioShares = !userWeights && portfolioSymbols.length >= 2 ? calculatePortfolioShares(portfolioSymbols) : null;

      const key = jobKey('ef', { t: tickersToUse, period, rfCountry, cap: maxWeightPct, w: userWeights || null, s: portfolioShares || null });
      latestKey.current = key;
      setActiveKey(key);
      const result = await runJob(key, {
        label: jobLabel || 'Efficient Frontier',
        route: jobRoute || `${window.location.pathname}${window.location.search}`,
      }, () => getEfficientFrontier(
        tickersToUse,
        period,
        100,
        portfolioSymbols,
        userWeights || undefined,
        rfCountry,
        maxWeightPct / 100,
        portfolioShares || undefined,
      ));
      if (!mounted.current || latestKey.current !== key) return;

      if (result.error) {
        setError(result.error);
        setData(null);
      } else {
        setData(result);
        // Default to the first strategy that needs no return forecast —
        // previously this opened on Max Sharpe, i.e. the least trustworthy one.
        const preferred = STRATEGY_DEFS.find((d) => !d.needsForecast && result[d.key])
          || STRATEGY_DEFS.find((d) => result[d.key]);
        setSelectedKey((preferred?.key as string) || 'risk_parity_portfolio');
      }
    } catch (err: any) {
      if (!mounted.current) return;
      console.error('Error fetching efficient frontier:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to calculate efficient frontier';
      setError(errorMessage);
      setData(null);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [period, rfCountry, userWeights, maxWeightPct, jobLabel, jobRoute]);

  // Only fetch frontier when period or rfCountry changes (if we already have calculated symbols)
  useEffect(() => {
    if (calculatedSymbols.length >= 2) {
      fetchFrontier(calculatedSymbols);
    }
  }, [period, calculatedSymbols, fetchFrontier]);

  // Run automatically on mount and whenever the incoming symbol set changes —
  // no manual "Update" click required. This only updates calculatedSymbols;
  // the effect above is what actually fetches, so there's no double-fetch.
  useEffect(() => {
    const tickersToUse = (propSymbols && propSymbols.length >= 2)
      ? propSymbols
      : (loadTickers().length >= 2 ? loadTickers() : []);
    if (tickersToUse.length >= 2 && tickersToUse.join(',') !== calculatedSymbols.join(',')) {
      setCalculatedSymbols(tickersToUse);
    }
  }, [propSymbols ? propSymbols.join(',') : '']);

  // Manual update function
  const handleUpdate = useCallback(() => {
    const tickersToUse = (propSymbols && propSymbols.length >= 2)
      ? propSymbols
      : (loadTickers().length >= 2 ? loadTickers() : []);

    if (tickersToUse && tickersToUse.length >= 2) {
      setCalculatedSymbols(tickersToUse);
      fetchFrontier(tickersToUse);
    } else {
      setError('At least 2 tickers required for efficient frontier');
      setData(null);
      setCalculatedSymbols([]);
    }
  }, [propSymbols, fetchFrontier]);

  // Remove symbol from list
  const handleRemoveSymbol = (symbolToRemove: string) => {
    const currentSymbols = (propSymbols && propSymbols.length >= 2)
      ? propSymbols
      : (loadTickers().length >= 2 ? loadTickers() : []);

    const updatedSymbols = currentSymbols.filter(s => s !== symbolToRemove);

    if (!propSymbols || propSymbols.length === 0) {
      localStorage.setItem(ANALYTICS_TICKERS_KEY, JSON.stringify(updatedSymbols));
    }

    if (calculatedSymbols.includes(symbolToRemove)) {
      const updatedCalculated = calculatedSymbols.filter(s => s !== symbolToRemove);
      setCalculatedSymbols(updatedCalculated);

      if (updatedCalculated.length < 2) {
        setData(null);
        setError('At least 2 tickers required for efficient frontier');
      }
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: string | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  // Get current symbols for display
  const currentSymbols = (propSymbols && propSymbols.length >= 2)
    ? propSymbols
    : (loadTickers().length >= 2 ? loadTickers() : []);

  // Stocks the selected strategy actually holds — the chart fades the rest.
  const heldSymbols = useMemo(() => {
    const pt = data?.[selectedKey as keyof EfficientFrontierResponse] as EfficientFrontierPoint | null | undefined;
    return new Set(Object.entries(pt?.weights || {}).filter(([, w]) => w >= SHOW_MIN).map(([sym]) => sym));
  }, [data, selectedKey]);

  if (!currentSymbols || currentSymbols.length < 2) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Efficient Frontier
        </Typography>
        <Alert severity="info">
          Add at least 2 stock tickers to view the efficient frontier analysis.
        </Alert>
      </Box>
    );
  }

  return (
    // No Paper/elevation here — this always renders inside the Analysis
    // Dashboard's own Paper, and a nested elevated Paper reads as a lighter
    // box-within-a-box in dark mode.
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Efficient Frontier Analysis
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
            Optimal portfolio allocations based on risk-return trade-offs
          </Typography>

          {/* Methodology info box */}
          <Box sx={{ mb: 2 }}>
            <Box
              onClick={() => setInfoOpen(o => !o)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                color: 'var(--bb-text-muted)', fontSize: 11, userSelect: 'none',
                '&:hover': { color: 'var(--bb-accent)' },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 11 }}>How are Expected Returns calculated?</Typography>
              {infoOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </Box>
            <Collapse in={infoOpen}>
              <Box sx={{
                mt: 1, p: 1.5, borderLeft: '3px solid var(--bb-accent)',
                bgcolor: 'rgba(255,140,0,0.06)', borderRadius: '0 4px 4px 0',
                maxWidth: 560,
              }}>
                <Typography sx={{ fontSize: 12, color: 'var(--bb-text-secondary)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--bb-accent)' }}>Expected Return</strong> is calculated from historical price data only — no analyst forecasts or forward-looking estimates are used.
                </Typography>
                <Box component="ul" sx={{ m: '6px 0 0 0', pl: 2.5, fontSize: 12, color: 'var(--bb-text-secondary)', lineHeight: 1.8 }}>
                  <li>Daily closing prices are fetched from Yahoo Finance for the selected period (6M / 1Y / 2Y / 5Y), converted into the basket's currency, and lined up on common dates</li>
                  <li>The average daily return is multiplied by 252 (trading days) to annualise it. For baskets whose exchanges close hours apart (e.g. London and New York) weekly returns are used instead, × 52, so every holding is measured over the same hours</li>
                  <li><strong>Limitation:</strong> a stock that happened to rise 80% last year will show 80% expected return — past performance is not a guarantee of future results</li>
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--bb-text-muted)', mt: 0.5 }}>
                  Volatility (risk) uses the same historical data. A longer period gives more stable estimates; a shorter period reflects recent conditions more closely.
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--bb-text-muted)', mt: 0.5 }}>
                  <strong>Min CVaR</strong> is estimated from only the worst 5% of days in the selected
                  period — on a 1Y lookback that's roughly a dozen trading days, so treat it as
                  directional rather than precise; a longer period gives a larger, more stable tail sample.
                </Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Symbols List with Remove Buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
              Equities:
            </Typography>
            {currentSymbols.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                size="small"
                onDelete={() => handleRemoveSymbol(symbol)}
                color={calculatedSymbols.includes(symbol) ? "primary" : "default"}
                variant={calculatedSymbols.includes(symbol) ? "filled" : "outlined"}
                sx={{
                  '& .MuiChip-deleteIcon': {
                    fontSize: '16px'
                  }
                }}
              />
            ))}
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleUpdate}
              disabled={loading || currentSymbols.length < 2}
              sx={{ ml: 1 }}
            >
              Update
            </Button>
            {onReset && currentSymbols.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  setCalculatedSymbols([]);
                  setData(null);
                  setError(null);
                  if (!propSymbols || propSymbols.length > 0) {
                    localStorage.setItem(ANALYTICS_TICKERS_KEY, JSON.stringify([]));
                  }
                  onReset();
                }}
                color="secondary"
                sx={{ ml: 1 }}
              >
                Reset all
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
          >
            <ToggleButton value="6mo">6M</ToggleButton>
            <ToggleButton value="1y">1Y</ToggleButton>
            <ToggleButton value="2y">2Y</ToggleButton>
            <ToggleButton value="5y">5Y</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Risk-Free Rate</InputLabel>
            <Select
              value={rfCountry}
              label="Risk-Free Rate"
              onChange={e => setRfCountry(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              {rfRates.length > 0 ? rfRates.map(opt => (
                <MenuItem key={opt.code} value={opt.code} sx={{ fontSize: '0.8rem' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                    <span>{opt.label}</span>
                    <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>
                      {opt.rate != null ? `${opt.live ? '' : '~'}${opt.rate.toFixed(2)}%` : opt.description}
                    </span>
                  </Box>
                </MenuItem>
              )) : (
                <MenuItem value="AUTO">Auto-detect</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Per-asset ceiling. The floor is 1/n — below that no fully-invested
              long-only portfolio exists — so the slider simply won't go there
              rather than letting the server silently raise it. */}
          <Box sx={{ minWidth: 200, px: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'var(--bb-text-muted)' }}>
                Max position
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace' }}>
                {maxWeightPct >= 100 ? 'uncapped' : `${maxWeightPct}%`}
              </Typography>
            </Box>
            <Slider
              size="small"
              value={maxWeightPct}
              min={capFloorPct}
              max={100}
              step={1}
              marks={[
                { value: capFloorPct, label: `${capFloorPct}%` },
                { value: 100, label: 'off' },
              ]}
              // Committed, not live: each change re-runs six optimisations.
              onChange={(_, v) => setPendingCapPct(v as number)}
              onChangeCommitted={(_, v) => setMaxWeightPct(v as number)}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => (v >= 100 ? 'off' : `${v}%`)}
              sx={{
                '& .MuiSlider-markLabel': { fontSize: '0.6rem', color: 'var(--bb-text-muted)' },
                mt: -0.5,
              }}
            />
            {pendingCapPct !== maxWeightPct && (
              <Typography sx={{ fontSize: '0.62rem', color: '#f59e0b', mt: -0.5 }}>
                release to recalculate
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {calculatedSymbols.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Click "Update" to calculate the efficient frontier with the current equities.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 460 }}>
            Calculating the frontier. This can take a few minutes. You can keep using the app;
            a pop-up will tell you when it's ready.
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : data ? (
        <>
          {data.fx_failed && data.fx_failed.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              No exchange rate was available for {data.fx_failed.join(', ')}, so {data.fx_failed.length === 1 ? 'its' : 'their'} figures
              are in {data.fx_failed.length === 1 ? 'its' : 'their'} own currency rather than {data.currency}. Treat {data.fx_failed.length === 1 ? 'its' : 'their'} position
              on the chart, and any allocation to {data.fx_failed.length === 1 ? 'it' : 'them'}, with care.
            </Alert>
          )}
          {/* Stacked: the chart spans the page, the holdings list sits below it.
              Side by side, a long list left the chart column empty below the
              fold; stacked, nothing sits beside the list, and it gets the width
              to show names untruncated. */}
          <Box sx={{ mt: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Risk / Return
                </Typography>
                {data.estimation && (
                  <Typography sx={{
                    fontSize: 10.5, color: 'var(--bb-text-muted)',
                    display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                  }}>
                    <Tooltip title="Ledoit-Wolf shrinkage pulls the sample covariance toward a structured target. 0 = sample matrix used as-is, 1 = fully replaced. Higher means less data relative to the number of assets.">
                      <span style={{ cursor: 'help' }}>
                        covariance shrinkage {(data.estimation.shrinkage_intensity * 100).toFixed(0)}%
                      </span>
                    </Tooltip>
                    <span>·</span>
                    <Tooltip title="No single position may exceed this. The most effective brake on the concentration mean-variance optimisation produces from noisy inputs.">
                      <span style={{ cursor: 'help' }}>
                        max position {(data.estimation.max_weight * 100).toFixed(0)}%
                        {data.estimation.max_weight_raised && ' (raised — too few assets for the requested cap)'}
                      </span>
                    </Tooltip>
                    {data.risk_free && (() => {
                      const r = data.risk_free!;
                      return (
                        <>
                          <span>·</span>
                          <Tooltip title={
                            `${r.tenor}${r.as_of ? `, as of ${r.as_of}` : ''}. Source: ${r.source}. Used for every Sharpe ratio and the Capital Market Line.` +
                            (r.live ? '' : ' No live short-rate feed exists for this market, so this is a static estimate.')
                          }>
                            <span style={{ cursor: 'help', color: r.live ? undefined : WARN }}>
                              risk-free {r.rate.toFixed(2)}%{r.live ? '' : ' (static)'}
                            </span>
                          </Tooltip>
                        </>
                      );
                    })()}
                    {data.sample && (() => {
                      const sm = data.sample!;
                      const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
                      return (
                        <>
                          <span>·</span>
                          <Tooltip title={
                            `Estimated from ${sm.observations} ${sm.frequency} returns, ${fmt(sm.start)} – ${fmt(sm.end)}. ` +
                            `${sm.frequency === 'weekly' ? 'Weekly' : 'Daily'} because ${sm.frequency_reason}.` +
                            (sm.limited_by ? ` The window starts late because ${sm.limited_by} has a shorter price history than the other holdings; every holding is measured over the same window.` : '')
                          }>
                            <span style={{ cursor: 'help', color: sm.limited_by ? WARN : undefined }}>
                              {sm.observations} {sm.frequency} returns{sm.limited_by ? ` · window limited by ${sm.limited_by}` : ''}
                            </span>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </Typography>
                )}
              </Box>
              <FrontierChart
                data={data} selectedKey={selectedKey} onSelectKey={setSelectedKey}
                heldSymbols={heldSymbols} hoveredSymbol={hoveredSymbol}
              />
              <ChartLegend data={data} />
            </Box>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Strategy picker. Colours match the dots on the chart. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {STRATEGY_DEFS.map(({ key, label, color }) => {
                  const pt = data[key] as EfficientFrontierPoint | null | undefined;
                  if (!pt) return null;
                  const sel = key === selectedKey;
                  return (
                    <Chip
                      key={key as string}
                      size="small"
                      onClick={() => setSelectedKey(key as string)}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          {label}
                          {pt.used_fallback && <WarningAmberIcon sx={{ fontSize: 13, color: WARN }} />}
                        </Box>
                      }
                      sx={{
                        fontSize: 12, fontWeight: sel ? 700 : 500, borderRadius: 1,
                        border: `1px solid ${sel ? color : 'var(--bb-border-primary)'}`,
                        bgcolor: sel ? `${color}22` : 'transparent',
                        color: sel ? color : 'var(--bb-text-secondary)',
                        '&:hover': { bgcolor: `${color}14` },
                      }}
                    />
                  );
                })}
              </Box>

              {/* Selected strategy: its headline figures, then what it holds. */}
              {(() => {
                const def = STRATEGY_DEFS.find((d) => d.key === selectedKey);
                const pt = data[selectedKey as keyof EfficientFrontierResponse] as EfficientFrontierPoint | null | undefined;
                if (!def || !pt) return null;
                return (
                  <Box sx={{
                    mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'var(--bb-bg-primary)',
                    border: '1px solid var(--bb-border-primary)', borderTop: `3px solid ${def.color}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: def.color }}>{def.label}</Typography>
                      {def.needsForecast && (
                        <Tooltip title={FORECAST_WARNING}>
                          <Typography sx={{
                            fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', px: 0.5,
                            borderRadius: 0.5, cursor: 'help', border: `1px solid ${WARN}`, color: WARN,
                          }}>
                            FORECAST
                          </Typography>
                        </Tooltip>
                      )}
                      {pt.used_fallback && (
                        <Tooltip title="Optimizer didn't converge for this strategy — showing an equal-weight fallback instead of a genuine solve.">
                          <Typography sx={{ fontSize: 11, color: WARN, display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'help' }}>
                            <WarningAmberIcon sx={{ fontSize: 14 }} /> equal-weight fallback
                          </Typography>
                        </Tooltip>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                        {[['Risk', `${pt.risk.toFixed(1)}%`], ['Return', `${pt.return.toFixed(1)}%`], ['Sharpe', pt.sharpe.toFixed(2)]].map(([k, v]) => (
                          <Box key={k} sx={{ textAlign: 'right' }}>
                            <Tooltip title={IN_SAMPLE_NOTE}>
                              <Typography sx={{ fontSize: 9, color: 'var(--bb-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'help' }}>
                                {k}*
                              </Typography>
                            </Tooltip>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>{v}</Typography>
                          </Box>
                        ))}
                      </Box>
                      {onApplyWeights && (
                        <Tooltip title="Save these weights to an active version of this model. The original model is not changed.">
                          <Button size="small" variant="contained" disableElevation
                            onClick={() => onApplyWeights(toHoldingWeights(pt.weights), {
                              strategy: def.label, capPct: maxWeightPct, period: period.toUpperCase(),
                              needsForecast: !!def.needsForecast, fallback: !!pt.used_fallback,
                            })}
                            sx={{ textTransform: 'none', fontSize: 12, ml: 1, bgcolor: def.color, '&:hover': { bgcolor: def.color, filter: 'brightness(1.1)' } }}>
                            Apply these weights…
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                    <HoldingsList data={data} selectedKey={selectedKey} onHoverSymbol={setHoveredSymbol} />
                  </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Benchmark Comparison */}
          {data.benchmarks && data.benchmarks.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Benchmark Comparison
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Portfolio / Benchmark</TableCell>
                      <TableCell align="right">Return (%)</TableCell>
                      <TableCell align="right">Risk (%)</TableCell>
                      <TableCell align="right">
                        <Tooltip title={`${IN_SAMPLE_NOTE} Benchmark rows are not fitted, so theirs is simply the trailing figure.`}>
                          <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>Sharpe Ratio</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* ── Optimised portfolios ── */}
                    {STRATEGY_DEFS.map(({ key, label, color }) => {
                      const pt = data[key] as EfficientFrontierPoint | null | undefined;
                      if (!pt) return null;
                      const labelChip = (
                        <Chip label={pt.used_fallback ? `${label} ⚠` : label} size="small" sx={{ bgcolor: color, color: 'white', fontWeight: 'bold' }} />
                      );
                      return (
                        <TableRow key={key as string} sx={{ bgcolor: `${color}12` }}>
                          <TableCell>
                            {pt.used_fallback ? (
                              <Tooltip title="Optimizer didn't converge for this strategy — showing an equal-weight fallback instead of a genuine solve.">
                                {labelChip}
                              </Tooltip>
                            ) : labelChip}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">{pt.return.toFixed(2)}%</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">{pt.risk.toFixed(2)}%</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">{pt.sharpe.toFixed(2)}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(() => {
                      const groups: { title: string; rows: BenchmarkAsset[] }[] = [
                        { title: 'Your portfolio', rows: data.benchmarks!.filter((bm) => bm.tier === 'portfolio' || bm.symbol === 'USER_PORTFOLIO') },
                        { title: data.base_currency ? `Local market · ${data.base_currency}` : 'Global market', rows: data.benchmarks!.filter((bm) => bm.tier === 'primary') },
                        { title: 'Global · for reference', rows: data.benchmarks!.filter((bm) => bm.tier === 'secondary' || (!bm.tier && bm.symbol !== 'USER_PORTFOLIO')) },
                      ];
                      return groups.filter((g) => g.rows.length).map((g) => (
                        <React.Fragment key={g.title}>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0.5, bgcolor: 'action.hover' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
                                {g.title}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {g.rows.map((bm) => {
                            // A different currency from the basket means the risk and return
                            // include currency moves the basket doesn't have: context, not a
                            // like-for-like yardstick.
                            const baseCcy = data.currency || data.base_currency;
                            const otherCcy = !bm.converted_from && !!(bm.currency && baseCcy && bm.currency !== baseCcy);
                            return (
                              <TableRow key={bm.symbol} sx={{ opacity: bm.tier === 'secondary' ? 0.8 : 1 }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: bm.tier === 'secondary' ? 500 : 700 }}>
                                      {bm.symbol === 'USER_PORTFOLIO' ? 'Your Portfolio' : bm.name}
                                    </Typography>
                                    {bm.symbol !== 'USER_PORTFOLIO' && (
                                      <Typography variant="caption" sx={{ color: 'var(--bb-text-muted)', fontFamily: 'monospace' }}>
                                        {bm.symbol}
                                      </Typography>
                                    )}
                                    {bm.currency && (
                                      <Tooltip title={
                                        bm.converted_from
                                          ? `Trades in ${bm.converted_from}; converted to ${baseCcy} at daily rates, so its figures are what a ${baseCcy} investor holding it would have seen — currency moves included.`
                                          : otherCcy ? `Priced in ${bm.currency}, not ${baseCcy}: its figures include currency moves your basket doesn't have.` : `Priced in ${bm.currency}.`
                                      }>
                                        <Typography variant="caption" sx={{
                                          px: 0.6, borderRadius: 0.5, fontSize: 10, fontWeight: 700, cursor: 'help',
                                          border: `1px solid ${otherCcy ? WARN : 'var(--bb-border-secondary)'}`,
                                          color: otherCcy ? WARN : 'var(--bb-text-secondary)',
                                        }}>
                                          {bm.converted_from ? `${bm.converted_from}→${baseCcy}` : bm.currency}
                                        </Typography>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right"><Typography variant="body2">{bm.return.toFixed(2)}%</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2">{bm.risk.toFixed(2)}%</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2" fontWeight="medium">{bm.sharpe.toFixed(2)}</Typography></TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                Every row is in {data.currency || 'the basket\'s currency'}: benchmarks that trade in another currency are converted
                at daily exchange rates, the same way as your holdings, so the comparison is like for like. The
                local-market benchmark is the closest yardstick — same market, dividends included (it's an ETF,
                adjusted the same way as your stocks); global rows are there for context.
              </Alert>
            </Box>
          )}
        </>
      ) : (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No efficient frontier data available. Please ensure all tickers have sufficient historical data.
        </Alert>
      )}
    </Box>
  );
};

export default EfficientFrontier;
